import { LoadingOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import { Dropdown, Input, Menu, Tooltip } from 'antd';
import Link from 'next/link';
import { useState } from 'react';
import styled from 'styled-components';

import { DeleteSvg } from '../../assets/icons';
import AnswerSvg from '../../assets/icons/AnswerSvg';
import FolderFancySvg from '../../assets/icons/FolderFancySvg';
import FolderSvg from '../../assets/icons/FolderSvg';
import MoreSvg from '../../assets/icons/MoreSvg';
import theme from '../../assets/theme';
import { useMetrics } from '../../hooks/useMetrics';
import useRouterMatch from '../../hooks/useRouterMatch';
import {
  Folder,
  FolderEditOrCreateDTO,
} from '../../services/TemplateService/Template.type';
import { Button } from '../_atoms/Button';
import { IconWrapper } from '../_atoms/IconWrapper';
import { AnimatedHoverContainer } from '../_molecules/AnimatedHoverContainer';
import BaseModal from '../_molecules/BaseModal';
import MoveToFolder, { MoveToFolderData } from './MoveToFolder/MoveToFolder';
import { MoveToTrashModal } from './MoveToTrashModal';

export const FoldersSection = ({
  folders,
  allFolders,
  deleteFolder,
  isDeletingFolder,
  saveFolder,
  isSavingFolder,
  type,
  workspaceId,
}: {
  folders: Folder[];
  allFolders?: Folder[];
  isDeletingFolder: boolean;
  deleteFolder: (f: Folder) => void;
  saveFolder: (d: {
    folderConfig: FolderEditOrCreateDTO;
    onSuccessFunction: () => void;
  }) => void;
  isSavingFolder: boolean;
  type: 'lessons' | 'workspace';
  workspaceId?: string;
}) => {
  const { t } = useTranslate();
  const { urlTenantId: tenantId } = useRouterMatch();
  const metrics = useMetrics();
  const [isDeletingData, setIsDeletingData] = useState<Folder | undefined>(undefined);
  const [isRenamingFolder, setIsRenamingFolder] = useState<Folder | undefined>(undefined);
  const [isMovingData, setIsMovingData] = useState<MoveToFolderData>();
  const renderMenu = (folder: Folder) => (
    <Menu>
      <Menu.Item key={'rename'} style={{ padding: 0 }}>
        <DropdownMenuItemButton
          onClick={() => {
            metrics.logEvent('Lessons.RenameFolder.Start');
            setIsRenamingFolder(folder);
          }}
        >
          <AnswerSvg /> <T keyName="Rename" defaultValue="Rename" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <Menu.Item
        key={'move'}
        style={{ padding: 0 }}
        onClick={() =>
          setIsMovingData({
            id: folder.id,
            type: 'folder',
            isPrivate: folder.isPrivate,
            title: folder.title,
            parentId: folder.parentId,
          })
        }
      >
        <DropdownMenuItemButton>
          <FolderSvg /> <T keyName="Move" defaultValue="Move" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <div
        key="divider-folder"
        style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
      />
      <Menu.Item key={'delete'} style={{ padding: 0, color: theme.colors.red }}>
        <DropdownMenuItemButton
          onClick={() => {
            metrics.logEvent('Lessons.DeleteFolder.Start');
            setIsDeletingData(folder);
          }}
        >
          <DeleteSvg /> <T keyName="Move to trash" defaultValue="Move to trash" />
        </DropdownMenuItemButton>
      </Menu.Item>
    </Menu>
  );

  const handleRename = () => {
    if (!isRenamingFolder) return;
    saveFolder({
      folderConfig: {
        ...isRenamingFolder,
        id: isRenamingFolder.id,
      } as FolderEditOrCreateDTO,
      onSuccessFunction: () => {
        setIsRenamingFolder(undefined);
      },
    });
  };

  const handleDeleteFolder = (f: Folder) => {
    deleteFolder(f);
    setIsDeletingData(undefined);
  };

  return (
    <SectionGridContainer>
      {isDeletingData && (
        <MoveToTrashModal
          handleDelete={() => handleDeleteFolder(isDeletingData)}
          deleteData={isDeletingData}
          isDeletingData={isDeletingFolder}
          folders={
            allFolders ? allFolders.filter(f => f.parentId === isDeletingData.id) : []
          }
          setDeleteData={setIsDeletingData}
        />
      )}
      {/* ------------ RENAME ----------------- */}
      {isRenamingFolder && (
        <BaseModal
          title={`${t('Rename folder', 'Rename')} ${
            isRenamingFolder?.title ? `"${isRenamingFolder.title}"` : ''
          }`}
          open
          onCancel={() => setIsRenamingFolder(undefined)}
        >
          <Input
            placeholder={t('New name')}
            autoFocus
            maxLength={40}
            value={isRenamingFolder.title || ''}
            disabled={isSavingFolder}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) =>
              setIsRenamingFolder({
                ...isRenamingFolder,
                title: event.target.value ? event.target.value : null,
              })
            }
            onPressEnter={() => handleRename()}
          ></Input>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '15px',
            }}
          >
            <Button
              type="tertiary"
              styles={{ border: 'none', color: theme.colors.black, boxShadow: 'none' }}
              onClick={() => {
                setIsRenamingFolder(undefined);
              }}
            >
              {t('Cancel')}
            </Button>
            <Button disabled={isSavingFolder} onClick={() => handleRename()}>
              {isSavingFolder ? (
                <>
                  <LoadingOutlined /> {t('Renaming')}
                </>
              ) : (
                t('Rename')
              )}
            </Button>
          </div>
        </BaseModal>
      )}
      {/* ------------ MOVE ----------------- */}
      {!!isMovingData && allFolders && (
        <MoveToFolder
          onCancel={() => setIsMovingData(undefined)}
          data={isMovingData}
          onDone={() => setIsMovingData(undefined)}
        />
      )}
      <SectionGrid>
        {folders.map(folder => {
          return (
            <AnimatedHoverContainer
              style={{ height: '100%', width: '100%', cursor: 'pointer' }}
              key={folder.id}
            >
              <Link
                href={`/${tenantId}/${
                  type === 'workspace' ? `workspace/${workspaceId}` : type
                }?parentFolderId=${folder.id}`}
              >
                <a>
                  <FolderItem>
                    <IconWrapper height="48px" width="48px">
                      <FolderFancySvg />
                    </IconWrapper>
                    <Tooltip title={folder.title} mouseEnterDelay={1.5}>
                      <FolderTitle>{folder.title}</FolderTitle>
                    </Tooltip>
                    <div
                      onClick={e => {
                        // Stop the click event from propagating to the parent div on click
                        e.stopPropagation();
                      }}
                    >
                      <Dropdown
                        overlay={renderMenu(folder)}
                        trigger={['click']}
                        placement={'bottomRight'}
                      >
                        <IconWrapper height="16px" width="16px">
                          <MoreSvg />
                        </IconWrapper>
                      </Dropdown>
                    </div>
                  </FolderItem>
                </a>
              </Link>
            </AnimatedHoverContainer>
          );
        })}
      </SectionGrid>
    </SectionGridContainer>
  );
};

const FolderTitle = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  margin-bottom: 0px;
  padding-left: 13px;
`;

const FolderItem = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 40px 1fr 16px;
  background: white;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  border-radius: 5px;
  padding: 10px 15px;
  align-items: center;

  span {
    font-weight: bold;
    color: ${theme.colors.black};
    font-size: 16px;
  }
`;

const SectionGridContainer = styled.div`
  padding: 10px 20px 40px 20px;
`;

const SectionGrid = styled.div`
  padding: 0px 0px 0px 0px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;

  @media (max-width: 1600px) {
    grid-template-columns: repeat(5, 1fr);
  }
  @media (max-width: 1300px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (max-width: 1000px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 800px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 500px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const DropdownMenuItemButton = styled.button`
  background: none;
  border: none;
  padding: 5px 12px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-weight: bold;
  &:disabled {
    cursor: not-allowed;
  }
  display: flex;
  align-items: center;
  svg {
    margin-right: 10px;
    margin-left: 5px;
    height: 16px;
    width: 16px;
  }
`;
